import { useEffect } from "react";
import { useSettings } from "@/context/SettingsContext";
import { useCollectionsQuery } from "@/lib/queries";

const JSON_LD_ID = "demj-jsonld-schema";

function upsertMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

// "£1,899" → 1899
function parseGbp(s) {
  const m = String(s || "").match(/([\d,]+(\.\d+)?)/);
  return m ? parseFloat(m[1].replace(/,/g, "")) : null;
}

function upsertJsonLd(graph) {
  let el = document.getElementById(JSON_LD_ID);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = JSON_LD_ID;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
}

export default function SEO() {
  const { settings } = useSettings();
  const { data: collections } = useCollectionsQuery();

  useEffect(() => {
    if (!settings) return;
    if (settings.meta_title) document.title = settings.meta_title;
    upsertMeta("name", "description", settings.meta_description);
    upsertMeta("property", "og:title", settings.meta_title);
    upsertMeta("property", "og:description", settings.meta_description);
    upsertMeta("property", "og:type", "website");
    if (settings.og_image) {
      upsertMeta("property", "og:image", settings.og_image);
      upsertMeta("name", "twitter:image", settings.og_image);
    }
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", settings.meta_title);
    upsertMeta("name", "twitter:description", settings.meta_description);
  }, [settings]);

  // JSON-LD (Organization + WebSite + ItemList of Products) — for Google rich results.
  useEffect(() => {
    if (!settings) return;
    const siteUrl = window.location.origin;
    const abs = (u) => {
      if (!u) return undefined;
      if (/^https?:\/\//i.test(u)) return u;
      return `${siteUrl}${u.startsWith("/") ? "" : "/"}${u}`;
    };
    const orgName = settings.brand_name || "Luxury Hair by DEMJ";
    const description = settings.meta_description ||
      "Premium human hair wigs, extensions and hair care. Trusted by clients worldwide.";
    const logo = abs(settings.logo_url || settings.og_image);

    const graph = [
      {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        name: orgName,
        url: siteUrl,
        ...(logo && { logo }),
        ...(settings.whatsapp_link && {
          sameAs: [settings.whatsapp_link, settings.instagram_link, settings.tiktok_link].filter(Boolean),
        }),
        ...(settings.contact_email && {
          contactPoint: [{
            "@type": "ContactPoint",
            contactType: "customer service",
            email: settings.contact_email,
            areaServed: "Worldwide",
          }],
        }),
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        url: siteUrl,
        name: orgName,
        description,
        publisher: { "@id": `${siteUrl}/#organization` },
      },
    ];

    // ItemList of Products — up to 40 in-stock items with sale-aware pricing.
    const list = (Array.isArray(collections) ? collections : [])
      .filter((c) => c && c.name && (c.stock_status || "").toLowerCase() !== "hidden")
      .slice(0, 40)
      .map((c, idx) => {
        const priceNumber = parseGbp(c.is_on_sale && c.sale_price_gbp ? c.sale_price_gbp : c.price_gbp);
        const availability = (c.stock_status || "").toLowerCase() === "in stock"
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock";
        return {
          "@type": "ListItem",
          position: idx + 1,
          item: {
            "@type": "Product",
            "@id": `${siteUrl}/#product-${c.id}`,
            name: c.name,
            ...(c.description && { description: c.description }),
            ...(c.image && { image: abs(c.image) }),
            brand: { "@type": "Brand", name: orgName },
            ...(c.category && { category: c.category }),
            ...(priceNumber != null && {
              offers: {
                "@type": "Offer",
                url: `${siteUrl}/?product=${c.id}`,
                priceCurrency: "GBP",
                price: priceNumber.toFixed(2),
                availability,
                seller: { "@id": `${siteUrl}/#organization` },
              },
            }),
          },
        };
      });

    if (list.length > 0) {
      graph.push({
        "@type": "ItemList",
        "@id": `${siteUrl}/#collections`,
        name: `${orgName} — Collections`,
        numberOfItems: list.length,
        itemListElement: list,
      });
    }

    upsertJsonLd(graph);
  }, [settings, collections]);

  return null;
}
